// Holds every text Collab shows in English and in Japanese, chooses the language to start in,
// words the reasons and file sizes Collab reports, and gives an avatar its initials and its hue.
//
// Every text Collab shows. English is the source; Japanese is translated from it.
// `{name}` marks a value filled in when shown.

export type Lang = 'en' | 'ja';

const en = {
  appName: 'Collab',
  language: 'Language',
  copy: 'Copy',
  copied: 'Copied.',
  copyFailed: 'Could not copy. Select the text and copy it.',

  formTitle: 'New place',
  tokenLabel: 'Token',
  makeLink: 'Create the link',
  collabLink: 'Collab link',
  openLink: 'Open',
  formIncomplete: 'Give a token.',

  enterTitle: 'Ready to join?',
  nameLabel: 'Your name',
  enter: 'Join',
  enterAgain: 'Join again',
  closedOutcome: 'You are not connected. {reason}',
  leftOutcome: 'You left.',
  newLink: 'New place',

  connecting: 'Connecting',
  connected: 'Connected over {transport}',
  reconnecting: 'Reconnecting after: {reason}',
  leave: 'Leave',

  people: 'People ({count})',
  you: '(you)',
  alone: "You're the only one here.",
  copyLink: 'Copy the link',

  board: 'Board',
  addNote: 'Add a note',
  addImage: 'Add an image',
  addLink: 'Add a link',
  linkUrl: 'Link',
  putOnBoard: 'Put on the board',
  fileGone: 'No longer shared',
  note: 'Note',
  deleteItem: 'Delete',
  moveItem: 'Drag to move',

  microphone: 'Microphone',
  camera: 'Camera',
  screen: 'Share screen',
  settings: 'Settings',
  more: 'More',
  close: 'Close',
  defaultDevice: 'Default',
  device: 'Device {n}',
  captureFailed: '{what} could not start: {message}',
  screenOf: '{name}: screen',
  fullscreen: 'Full screen',
  pictureInPicture: 'Picture-in-picture',

  files: 'Files',
  shareFiles: 'Share files',
  filesEmpty: 'No files are shared.',
  tooBig: '{name} is over 16 MiB and was not shared.',
  sharedBy: '{size}, shared by {name}',
  open: 'Open',
  save: 'Save',
  removeFile: 'Remove from the place',
  readFailed: 'Could not read {name}.',

  'invalid-token': 'The link\'s token is not valid, or its key was deleted ({code}).',
  expired: 'The access has expired ({code}).',
  removed: 'You were removed ({code}).',
  'path-closed': 'This place was closed ({code}).',
  full: 'The project has as many connections as it allows ({code}).',
  stopped: "The project used this month's data ({code}).",
  unavailable: 'The Relay is not available now ({code}).',
  network: 'The connection was lost, or the Relay could not be reached ({code}).',
};

export type Key = keyof typeof en;

const ja: Record<Key, string> = {
  appName: 'Collab',
  language: '表示言語',
  copy: 'コピー',
  copied: 'コピーしました。',
  copyFailed: 'コピーできませんでした。文字を選んでコピーしてください。',

  formTitle: '新しい場所',
  tokenLabel: 'トークン',
  makeLink: 'リンクを作る',
  collabLink: 'Collab のリンク',
  openLink: '開く',
  formIncomplete: 'トークンを入れてください。',

  enterTitle: '参加しますか？',
  nameLabel: 'あなたの名前',
  enter: '参加する',
  enterAgain: 'もう一度参加する',
  closedOutcome: 'つながっていません。{reason}',
  leftOutcome: '退出しました。',
  newLink: '新しい場所を作る',

  connecting: '接続中',
  connected: '{transport} で接続中',
  reconnecting: '再接続しています。理由: {reason}',
  leave: '退出',

  people: '参加者（{count}）',
  you: '（あなた）',
  alone: 'ここにいるのはあなただけです。',
  copyLink: 'リンクをコピー',

  board: 'ボード',
  addNote: 'メモを追加',
  addImage: '画像を追加',
  addLink: 'リンクを追加',
  linkUrl: 'リンク',
  putOnBoard: 'ボードに置く',
  fileGone: '共有されていません',
  note: 'メモ',
  deleteItem: '消す',
  moveItem: 'ドラッグして動かす',

  microphone: 'マイク',
  camera: 'カメラ',
  screen: '画面を共有',
  settings: '設定',
  more: 'その他',
  close: '閉じる',
  defaultDevice: '既定',
  device: '機器 {n}',
  captureFailed: '{what}を開始できませんでした: {message}',
  screenOf: '{name}: 画面',
  fullscreen: '全画面',
  pictureInPicture: 'ピクチャ・イン・ピクチャ',

  files: 'ファイル',
  shareFiles: 'ファイルを共有',
  filesEmpty: '共有されているファイルはありません。',
  tooBig: '{name} は 16 MiB を超えるため共有しませんでした。',
  sharedBy: '{size}、{name} が共有',
  open: '開く',
  save: '保存',
  removeFile: 'この場から削除',
  readFailed: '{name} を読めませんでした。',

  'invalid-token': 'リンクのトークンが有効ではないか、そのキーが削除されています（{code}）。',
  expired: 'アクセスの期限が切れています（{code}）。',
  removed: '退出させられました（{code}）。',
  'path-closed': 'この場所は閉じられました（{code}）。',
  full: 'プロジェクトの接続数が上限に達しています（{code}）。',
  stopped: 'プロジェクトが今月の通信量を使い切りました（{code}）。',
  unavailable: 'Relay がいま使えません（{code}）。',
  network: '接続が切れたか、Relay に届きませんでした（{code}）。',
};

export const texts: Record<Lang, Record<Key, string>> = { en, ja };

/** The text for `key` in `lang`, with each `{name}` in `values` filled in. */
export function text(lang: Lang, key: Key, values: Record<string, string | number> = {}): string {
  return texts[lang][key].replace(/\{(\w+)\}/gu, (whole, name: string) => (name in values ? String(values[name]) : whole));
}

/** The SDK's reason names that have a text. */
const REASONS = new Set<string>(['invalid-token', 'expired', 'removed', 'path-closed', 'full', 'stopped', 'unavailable', 'network']);

/** The text of a reason, naming it; an unknown name counts as `network`. */
export function reasonText(lang: Lang, name: string): string {
  return text(lang, (REASONS.has(name) ? name : 'network') as Key, { code: name });
}

/** Japanese when the browser prefers it, English otherwise; a stored choice wins (as Console does). */
export function startLanguage(stored: string | null, preferred: readonly string[]): Lang {
  if (stored === 'en' || stored === 'ja') return stored;
  return preferred[0]?.toLowerCase().startsWith('ja') ? 'ja' : 'en';
}

/** A size in bytes, as B, KiB or MiB. */
export function formatSize(bytes: number, lang: Lang): string {
  const number = (value: number) => new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(value);
  if (bytes < 1024) return `${number(bytes)} B`;
  if (bytes < 1024 * 1024) return `${number(bytes / 1024)} KiB`;
  return `${number(bytes / 1024 / 1024)} MiB`;
}

/** The initials shown for `name`: the first character of each of its first two words, upper-cased; "?" for none. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean).slice(0, 2);
  return words.map(word => [...word][0]!.toUpperCase()).join('') || '?';
}

/** A hue, 0 to 359, that is the same for the same `seed`. */
export function hueOf(seed: string): number {
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.codePointAt(0)!) | 0;
  return Math.abs(hash) % 360;
}
